import { ApiResult, FieldType, isEmptyOrWhitespace, ViewModelBase } from "@shoothill/core";
import { runInAction, observable, action } from "mobx";
import {
    DailyProgrammeProgressUpdateV2Model,
    UpsertDailyProgrammeProgressUpdateV2RequestDTO,
    UpsertDailyProgrammeProgressUpdateV2ResponseDTO,
} from "./DailyProgrammeProgressUpdateV2Model";
import { DailyProgrammeProgressUpdateV2ItemViewModel } from "./DailyProgrammeProgressUpdateV2ItemViewModel";
import { AppUrls } from "AppUrls";
import { ServerViewModel } from "Globals/ViewModels/ServerViewModel";
import { DailyProgrammeProgressUpdateV2ItemDTO, DailyProgrammeProgressUpdateV2ItemModel } from "./DailyProgrammeProgressUpdateV2ItemModel";

export class DailyProgrammeProgressUpdateV2ViewModel extends ViewModelBase<DailyProgrammeProgressUpdateV2Model> {
    public projectId: string = "";
    @observable public newItem: DailyProgrammeProgressUpdateV2ItemViewModel = new DailyProgrammeProgressUpdateV2ItemViewModel(new DailyProgrammeProgressUpdateV2ItemModel());
    @observable public items: DailyProgrammeProgressUpdateV2ItemViewModel[] = [];
    public constructor(projectId: string | null, date: any, formId: string | null) {
        super(new DailyProgrammeProgressUpdateV2Model(), false);
        this.setDecorators(DailyProgrammeProgressUpdateV2Model);
        this.model.projectId = projectId!;
        this.model.id = formId!;
        this.model.createdDate = date.toISOString();
        if (!isEmptyOrWhitespace(this.model.id) || this.model.id === null) {
            this.loadWithRelated();
        }
    }

    @observable public errorMessage: string = "";
    @observable public showSaveButton: boolean = false;
    @observable public newItemError: string = "";

    public server: ServerViewModel = new ServerViewModel();

    @action
    public reset = () => {
        this.model.reset();
        this.items = [];
        this.server.reset();
        this.newItem.reset();
        this.newItemError = "";
        this.showSaveButton = false;
    };

    @action
    public addItem = async () => {
        if (await this.newItem.isModelValid()) {
            runInAction(() => {
                const itemModel = new DailyProgrammeProgressUpdateV2ItemModel();
                itemModel.fromDto(this.newItem.model.toDto());
                itemModel.createdDate = this.model.createdDate;
                const itemVM = new DailyProgrammeProgressUpdateV2ItemViewModel(itemModel);

                this.items.push(itemVM);
                this.model.items = this.items.map((vm) => vm.model);
                this.newItem.reset();
                this.showSaveButton = true;
            });
        } else {
            // Do nothing
        }
    };

    @action
    public setItems = async (items: DailyProgrammeProgressUpdateV2ItemDTO[]) => {
        items.forEach((item) => {
            runInAction(() => {
                const itemModel = new DailyProgrammeProgressUpdateV2ItemModel();
                itemModel.fromDto(item);
                const itemVM = new DailyProgrammeProgressUpdateV2ItemViewModel(itemModel);

                this.items.push(itemVM);
                this.model.items = this.items.map((vm) => vm.model);
            });
        });
    };

    @action
    public loadWithRelated = async (): Promise<void> => {
        this.setIsLoading(true);
        const request = {
            date: this.model.createdDate,
            projectId: this.model.projectId,
            formId: this.model.id,
        };
        let apiResult = await this.Post<any>(
            AppUrls.Server.Projects.Construction.ProgrammeUpdates.DailyProgrammeProgressUpdateV2.GetDailyProgrammeProgressUpdateV2AndRelatedById,
            request,
        );
        if (apiResult && apiResult.wasSuccessful) {
            runInAction(() => {
                if (apiResult.payload.dailyProgrammeProgressUpdateV2) {
                    this.model.fromDto(apiResult.payload.dailyProgrammeProgressUpdateV2);
                }

                this.setItems(apiResult.payload.dailyProgrammeProgressUpdateV2Items);
            });
        }
        this.setIsLoading(false);
    };

    @action
    public upsert = async (e?: any): Promise<ApiResult<UpsertDailyProgrammeProgressUpdateV2RequestDTO>> => {
        e?.preventDefault();

        if (await this.isMyModelValid()) {
            const model: any = this.model.toDto();
            let dailyProgrammeProgressUpdateV2Items: DailyProgrammeProgressUpdateV2ItemDTO[] = [];
            this.model.items.forEach((item) => {
                const dailyProgrammeProgressUpdateV2Item: DailyProgrammeProgressUpdateV2ItemDTO = {
                    id: item.id,
                    dailyProgrammeProgressUpdateV2Id: item.dailyProgrammeProgressUpdateV2Id,
                    trade: item.trade,
                    activity: item.activity,
                    percentageCompletion: item.percentageCompletion,
                    createdByUserId: item.createdByUserId,
                    createdDate: item.createdDate,
                    lastUpdatedByUserId: item.lastUpdatedByUserId,
                    lastUpdatedDate: item.lastUpdatedDate,
                    rowVersion: item.rowVersion,
                };
                dailyProgrammeProgressUpdateV2Items.push(dailyProgrammeProgressUpdateV2Item);
            });

            const request: UpsertDailyProgrammeProgressUpdateV2RequestDTO = {
                dailyProgrammeProgressUpdateV2: model,
                dailyProgrammeProgressUpdateV2Items: dailyProgrammeProgressUpdateV2Items,
            };

            let apiResult = await this.Post<UpsertDailyProgrammeProgressUpdateV2RequestDTO>(
                AppUrls.Server.Projects.Construction.ProgrammeUpdates.DailyProgrammeProgressUpdateV2.UpsertDailyProgrammeProgressUpdateV2,
                request,
            );

            if (apiResult.wasSuccessful) {
                runInAction(() => {
                    this.model.fromDto(apiResult.payload.dailyProgrammeProgressUpdateV2);
                    this.setItems(apiResult.payload.dailyProgrammeProgressUpdateV2Items);
                });
            }
            return apiResult;
        } else {
            this.errorMessage = "Form is not valid";
            return Promise.reject();
        }
    };

    /**
     * Custom model validation function. Validates child category models and its children
     * @returns True if model is valid, false if not.
     */
    public isMyModelValid = async (): Promise<boolean> => {
        let isValid = true;

        if (this.items.length === 0) {
            isValid = false;
            // this.setSnackMessage("Add at least one line before saving.");
            // this.setSnackType(this.SNACKERROR);
            // this.setSnackbarState(true);
        }

        // JC: Changed forEach into for loop as the await seems to have issues with forEach.
        for (let i = 0; i < this.items.length; i++) {
            let item = this.items[i];

            // Validate each child item.
            if ((await item.isModelValid()) === false) {
                isValid = false;
            }
        }

        if ((await this.isModelValid()) === false) {
            isValid = false;
        }

        return isValid;
    };

    public async isFieldValid(fieldName: keyof FieldType<DailyProgrammeProgressUpdateV2Model>): Promise<boolean> {
        let { isValid, errorMessage } = await this.validateDecorators(fieldName);
        this.setError(fieldName, errorMessage);
        this.setValid(fieldName, isValid);
        return isValid;
    }
    public afterUpdate: undefined;
    public beforeUpdate: undefined;
}
